import React, { FC } from 'react';
import { ChatDotsFill, Clock, PersonCheckFill, ShieldLockFill } from 'react-bootstrap-icons';
import { Faq, HeroImage } from '../../customFrontend/parts';
import { SiteManifest, SiteManifestPage } from '../../customFrontend/types';
import { CityTiles, Cta, FinalCta, Notifications, SiteFooter, TopicCards } from './Chrome';
import { ChatappCity, ChatappHomeCopy, ChatappLabels, ChatappTopic } from './types';
import './chatapp.scss';

const REASON_ICONS = [ChatDotsFill, Clock, ShieldLockFill, PersonCheckFill];

interface Props {
  site: SiteManifest;
  page: SiteManifestPage;
  copy: ChatappHomeCopy;
  cities: ChatappCity[];
  labels: ChatappLabels;
  /** Topic pages, linked below the city tiles and in the footer row */
  topics?: ChatappTopic[];
}

/** The chatapp brand's home page: hero with app notifications, steps (with a photo when the copy has one), reasons, city tiles, FAQ. */
const BrandHome: FC<Props> = ({ site, page, copy, cities, labels, topics = [] }) => (
  <>
    <section className="cx-hero cx-hero--home">
      <div className="container cx-hero-grid">
        <div className="cx-hero-text">
          <p className="cx-eyebrow"><span className="cx-dot" aria-hidden="true" /> {copy.eyebrow}</p>
          <h1>{copy.h1} <span className="cx-accent">{copy.h1Accent}</span></h1>
          <p className="cx-lead">{copy.lead}</p>
          <Cta>{copy.cta}</Cta>
        </div>
        <div className="cx-hero-media">
          <figure className="cx-frame">
            <HeroImage page={page} className="cx-frame-img" />
          </figure>
          <Notifications site={site} items={copy.notifications} now={labels.now} />
        </div>
      </div>
    </section>

    <section className="cx-section">
      <div className="container cx-narrow">
        <h2>{copy.about.title}</h2>
        {copy.about.paragraphs.map((paragraph, i) => <p key={i}>{paragraph}</p>)}
      </div>
    </section>

    <section className="cx-section cx-section--soft">
      <div className="container">
        <h2 className="text-center">{copy.steps.title}</h2>
        <div className={copy.steps.image ? 'cx-steps-layout' : undefined}>
          {copy.steps.image && (
            <figure className="cx-frame cx-steps-photo">
              <img
                className="cx-frame-img"
                src={copy.steps.image.src}
                srcSet={copy.steps.image.srcSet}
                sizes={copy.steps.image.sizes}
                width={copy.steps.image.width}
                height={copy.steps.image.height}
                alt={copy.steps.image.alt}
                loading="lazy"
                decoding="async"
              />
            </figure>
          )}
          <ol className="cx-steps">
            {copy.steps.items.map((step) => (
              <li key={step.title} className="cx-step">
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    <section className="cx-section">
      <div className="container">
        <h2 className="text-center">{copy.reasons.title}</h2>
        <div className="cx-reasons">
          {copy.reasons.items.map((reason, i) => {
            const Icon = REASON_ICONS[i % REASON_ICONS.length]!;
            return (
              <div key={reason.title} className="cx-reason">
                <span className="cx-reason-icon"><Icon aria-hidden="true" /></span>
                <h3>{reason.title}</h3>
                <p>{reason.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="cx-section cx-section--soft">
      <div className="container">
        <h2 className="text-center">{copy.cities.title}</h2>
        <p className="cx-section-intro">{copy.cities.intro}</p>
        <CityTiles cities={cities} labels={labels} />
        <TopicCards topics={topics} />
      </div>
    </section>

    <section className="cx-section">
      <div className="container cx-narrow">
        <Faq title={copy.faq.title} items={copy.faq.items} />
      </div>
    </section>

    <FinalCta {...copy.final} />
    <SiteFooter site={site} cities={cities} labels={labels} topics={topics} />
  </>
);

export default BrandHome;
